import logger from '../../utils/logger';

// Geolocation utilities for generating grid points and calculating distances

const EARTH_RADIUS_KM = 6371;

function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

function toDegrees(radians) {
  return radians * (180 / Math.PI);
}

export function calculateDestination(start, bearingDegrees, distanceKm) {
  const lat1 = toRadians(start.lat);
  const lng1 = toRadians(start.lng);
  const bearing = toRadians(bearingDegrees);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distanceKm / EARTH_RADIUS_KM) +
      Math.cos(lat1) * Math.sin(distanceKm / EARTH_RADIUS_KM) * Math.cos(bearing)
  );

  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(distanceKm / EARTH_RADIUS_KM) * Math.cos(lat1),
      Math.cos(distanceKm / EARTH_RADIUS_KM) - Math.sin(lat1) * Math.sin(lat2)
    );

  return {
    lat: toDegrees(lat2),
    lng: toDegrees(lng2),
  };
}

export function calculateDistance(point1, point2) {
  const lat1 = toRadians(point1.lat);
  const lng1 = toRadians(point1.lng);
  const lat2 = toRadians(point2.lat);
  const lng2 = toRadians(point2.lng);

  const dLat = lat2 - lat1;
  const dLng = lng2 - lng1;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

export function generateGridPoints(center, radiusKm = 1, gridSize = 5) {
  // Validate inputs
  if (!center || typeof center.lat !== 'number' || isNaN(center.lat)) {
    logger.error('❌ Invalid center.lat:', center);
    throw new Error(`Invalid center.lat: ${center?.lat}`);
  }
  if (!center || typeof center.lng !== 'number' || isNaN(center.lng)) {
    logger.error('❌ Invalid center.lng:', center);
    throw new Error(`Invalid center.lng: ${center?.lng}`);
  }
  if (typeof radiusKm !== 'number' || isNaN(radiusKm) || radiusKm <= 0) {
    logger.error('❌ Invalid radiusKm:', radiusKm);
    throw new Error(`Invalid radiusKm: ${radiusKm}`);
  }
  if (typeof gridSize !== 'number' || isNaN(gridSize) || gridSize <= 0) {
    logger.error('❌ Invalid gridSize:', gridSize);
    throw new Error(`Invalid gridSize: ${gridSize}`);
  }

  logger.log('✅ Generating grid with valid inputs:', { center, radiusKm, gridSize });

  const points = [];
  const stepSize = (2 * radiusKm) / (gridSize - 1);

  const startLat = center.lat + (radiusKm / EARTH_RADIUS_KM) * (180 / Math.PI);
  const startLng =
    center.lng -
    (radiusKm / (EARTH_RADIUS_KM * Math.cos(toRadians(center.lat)))) *
      (180 / Math.PI);

  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const latOffset = (-row * stepSize) / EARTH_RADIUS_KM * (180 / Math.PI);
      const lngOffset =
        (col * stepSize) /
        (EARTH_RADIUS_KM * Math.cos(toRadians(center.lat))) *
        (180 / Math.PI);

      points.push({
        lat: startLat + latOffset,
        lng: startLng + lngOffset,
        gridX: col,
        gridY: row,
        id: `grid-${row}-${col}`,
      });
    }
  }

  return points;
}

export function calculateGridBounds(gridPoints) {
  const lats = gridPoints.map((p) => p.lat);
  const lngs = gridPoints.map((p) => p.lng);

  return {
    north: Math.max(...lats),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    west: Math.min(...lngs),
  };
}

export function getGridLabel(gridX, gridY) {
  const letters = 'ABCDE';
  return `${letters[gridY]}${gridX + 1}`;
}

export function isValidCoordinates(coords) {
  return (
    coords &&
    typeof coords.lat === 'number' &&
    typeof coords.lng === 'number' &&
    coords.lat >= -90 &&
    coords.lat <= 90 &&
    coords.lng >= -180 &&
    coords.lng <= 180
  );
}

export async function reverseGeocode(coords, apiKey, useMockData = false) {
  try {
    // Validate coordinates first
    if (!isValidCoordinates(coords)) {
      logger.warn('Invalid coordinates provided to reverseGeocode:', coords);
      return 'Invalid Location';
    }

    if (useMockData) {
      return `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`;
    }

    // Use OpenStreetMap Nominatim instead of Google Geocoding API
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1&accept-language=en`,
      {
        headers: {
          'User-Agent': 'Visibeen-Heatmap-Tool/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Geocoding API request failed: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.display_name) {
      throw new Error('No geocoding results found');
    }

    return extractCleanAddressFromNominatimResults(data);
  } catch (error) {
    logger.warn('Reverse geocoding failed:', error);
    return `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)} (Location Unknown)`;
  }
}

function extractCleanAddressFromNominatimResults(data) {
  try {
    const address = data.address || {};
    let cleanAddress = '';

    // Extract relevant address components from Nominatim response
    const locality = address.city || address.town || address.village || address.hamlet || '';
    const subLocality = address.suburb || address.neighbourhood || address.quarter || '';
    const administrativeArea = address.state || address.province || address.region || '';
    const postalCode = address.postcode || '';
    const country = address.country || '';

    // Build clean address string
    if (locality) {
      cleanAddress += locality;
    }
    if (subLocality) {
      cleanAddress += cleanAddress ? `, ${subLocality}` : subLocality;
    }
    if (postalCode) {
      cleanAddress += cleanAddress ? ` - ${postalCode}` : postalCode;
    }
    if (administrativeArea) {
      cleanAddress += cleanAddress ? `, ${administrativeArea}` : administrativeArea;
    }
    if (country) {
      cleanAddress += cleanAddress ? `, ${country}` : country;
    }

    // Fallback to display_name if no structured address
    if (!cleanAddress && data.display_name) {
      let fallbackAddress = data.display_name;
      // Clean up common unwanted parts
      fallbackAddress = fallbackAddress.replace(/\d{6},?\s*/g, ''); // Remove postal codes
      fallbackAddress = fallbackAddress.replace(/Unnamed Road,?\s*/gi, '');
      cleanAddress = fallbackAddress;
    }

    return cleanAddress || 'Unknown Location';
  } catch (error) {
    logger.warn('Error extracting clean address from Nominatim response:', error);
    return data.display_name || 'Unknown Location';
  }
}

function extractCleanAddressFromGoogleResults(results) {
  try {
    let locality = '';
    let subLocality = '';
    let administrativeArea = '';
    let postalCode = '';
    let country = '';

    for (const result of results) {
      if (result.address_components) {
        for (const component of result.address_components) {
          const types = component.types;

          if (types.includes('locality') && !locality) {
            locality = component.long_name;
          } else if (types.includes('administrative_area_level_2') && !locality) {
            locality = component.long_name;
          } else if ((types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('neighborhood')) && !subLocality) {
            subLocality = component.long_name;
          } else if (types.includes('administrative_area_level_1') && !administrativeArea) {
            administrativeArea = component.long_name;
          } else if (types.includes('postal_code') && !postalCode) {
            postalCode = component.long_name;
          } else if (types.includes('country') && !country) {
            country = component.long_name;
          }
        }
      }
    }

    let cleanAddress = '';

    if (locality) {
      cleanAddress += locality;
    }
    if (subLocality) {
      cleanAddress += cleanAddress ? `, ${subLocality}` : subLocality;
    }

    if (postalCode) {
      cleanAddress += cleanAddress ? ` - ${postalCode}` : postalCode;
    }

    if (administrativeArea) {
      cleanAddress += cleanAddress ? `, ${administrativeArea}` : administrativeArea;
    }

    if (country) {
      cleanAddress += cleanAddress ? `, ${country}` : country;
    }

    if (!cleanAddress && results.length > 0) {
      let fallbackAddress = results[0].formatted_address || '';
      fallbackAddress = fallbackAddress.replace(/[A-Z0-9]{4}\+[A-Z0-9]{2},?\s*/g, '');
      fallbackAddress = fallbackAddress.replace(/Unnamed Road,?\s*/gi, '');
      cleanAddress = fallbackAddress;
    }

    return cleanAddress || 'Unknown Location';
  } catch (error) {
    logger.warn('Error extracting clean address from Google response:', error);
    return results[0]?.formatted_address || 'Unknown Location';
  }
}

export async function generateGridPointsWithAddresses(
  center,
  radiusKm = 1,
  gridSize = 5,
  apiKey = '',
  useMockData = false,
  onProgress
) {
  const points = generateGridPoints(center, radiusKm, gridSize);
  const pointsWithAddresses = [];

  for (let i = 0; i < points.length; i++) {
    const point = points[i];

    try {
      const address = await reverseGeocode(point, apiKey, useMockData);
      pointsWithAddresses.push({
        ...point,
        address,
      });

      if (onProgress) {
        onProgress(i + 1, points.length, address);
      }

      if (i < points.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      logger.warn(`Failed to get address for point ${point.id}:`, error);
      pointsWithAddresses.push({
        ...point,
        address: `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)} (Address lookup failed)`,
      });
    }
  }

  return pointsWithAddresses;
}
